// apps/web/src/features/call-center/CallCenterLeadForm.tsx
//
// Reusable intake form body for the external `call_center_agent` boundary
// (#1398, extracted from the old single-form page #1259). Hosted inside the
// New-lead `EditorDrawer`. Same 4 fields, same `optionalText` coercion, same
// blind submit via `useSubmitCallCenterLead`.
//
// DANGER: this is the EXTERNAL agent boundary. The accepted response is a
// bare `{ accepted: true }` — never render lead id, public code, dedupe
// state, assigned manager, task, or stage here.
//
// WHY (#1398): the form surfaces `formState.isDirty` to its parent (for the
// drawer's unsaved-changes guard) and calls `onSubmitted` on success so the
// parent toasts + closes + invalidates. An own-history phone collision
// (`CALL_CENTER_DUPLICATE_OWN_PHONE`, #1397) renders a localized inline error
// and KEEPS the form open — matched by `code`, never message text. All other
// errors fall back to the generic `errorMessage()`.
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import {
  CALL_CENTER_LEAD_AUDIO_URL_MAX,
  CALL_CENTER_LEAD_NAME_MAX,
  CallCenterLeadIntakeSubmissionSchema,
  ErrorCode,
  type CallCenterLeadIntakeSubmission,
} from '@magimp/shared';
import { useSubmitCallCenterLead } from '@magimp/shared-hooks';
import { Button, FormRow, Input, PhoneInput, Textarea } from '../../ui/index.js';
import { ApiClientError, errorMessage } from '../../api/errors.js';
import { phoneError } from '../../shared/index.js';

// WHY: optional schema fields reject `''` (audioUrl must be a URL) — coerce an
// empty control value to `undefined` so a left-blank optional field validates.
function optionalText(v: string): string | undefined {
  return v === '' ? undefined : v;
}

const DEFAULTS: CallCenterLeadIntakeSubmission = {
  phone: '',
  name: '',
  comment: undefined,
  audioUrl: undefined,
};

export interface CallCenterLeadFormProps {
  /** Bubbles `formState.isDirty` so a host drawer can wire its dirty guard. */
  onDirtyChange?: (dirty: boolean) => void;
  /** Called once the submit succeeds (host toasts + closes + invalidates). */
  onSubmitted: () => void;
}

export function CallCenterLeadForm({ onDirtyChange, onSubmitted }: CallCenterLeadFormProps) {
  const { t } = useTranslation('callCenter');
  const submit = useSubmitCallCenterLead();
  // WHY (#1397/#1398): the own-history collision is a per-submit, code-matched
  //   inline error that must persist across re-renders (drawer stays open). A
  //   generic fallback covers every other error.
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<CallCenterLeadIntakeSubmission>({
    resolver: zodResolver(CallCenterLeadIntakeSubmissionSchema),
    defaultValues: DEFAULTS,
  });

  // Bubble dirty to the host (drawer guard); clear it on unmount so a closed
  // drawer never leaves a stale-dirty signal behind.
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  const onSubmit = (values: CallCenterLeadIntakeSubmission) => {
    setSubmitError(null);
    submit.mutate(values, {
      onSuccess: () => onSubmitted(),
      onError: (err) => {
        // WHY (#1397/D-7): the own-history collision gets its OWN localized
        //   copy and keeps the drawer open; everything else falls back to the
        //   generic error map. Match on `code`, never message text.
        if (
          err instanceof ApiClientError &&
          err.code === ErrorCode.CALL_CENTER_DUPLICATE_OWN_PHONE
        ) {
          setSubmitError(t('selfService.ownDuplicate'));
          return;
        }
        setSubmitError(errorMessage(err));
      },
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <FormRow
        label={t('intake.fields.phone')}
        error={phoneError(errors.phone?.message, t('intake.errors.invalidPhone'))}
      >
        <Controller
          control={control}
          name="phone"
          render={({ field }) => (
            <PhoneInput
              value={field.value ?? ''}
              onChange={field.onChange}
              invalid={!!errors.phone}
              testId="cc-intake-phone"
            />
          )}
        />
      </FormRow>
      <FormRow label={t('intake.fields.name')} error={errors.name?.message}>
        <Controller
          control={control}
          name="name"
          render={({ field }) => (
            <Input
              value={field.value ?? ''}
              onChange={field.onChange}
              invalid={!!errors.name}
              maxLength={CALL_CENTER_LEAD_NAME_MAX}
            />
          )}
        />
      </FormRow>
      <FormRow label={t('intake.fields.comment')} error={errors.comment?.message}>
        <Controller
          control={control}
          name="comment"
          render={({ field }) => (
            <Textarea
              value={field.value ?? ''}
              onChange={(v) => field.onChange(optionalText(v))}
              rows={4}
              ariaLabel={t('intake.fields.comment')}
            />
          )}
        />
      </FormRow>
      <FormRow
        label={t('intake.fields.audioUrl')}
        error={errors.audioUrl ? t('intake.errors.invalidAudioUrl') : undefined}
      >
        <Controller
          control={control}
          name="audioUrl"
          render={({ field }) => (
            <Input
              value={field.value ?? ''}
              onChange={(v) => field.onChange(optionalText(v))}
              invalid={!!errors.audioUrl}
              maxLength={CALL_CENTER_LEAD_AUDIO_URL_MAX}
            />
          )}
        />
      </FormRow>
      {submitError ? (
        <p
          role="alert"
          data-testid="cc-intake-submit-error"
          style={{
            fontSize: 'var(--fz-13)',
            color: 'var(--status-error-fg)',
            margin: '0 0 var(--s-3)',
          }}
        >
          {submitError}
        </p>
      ) : null}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--s-2)' }}>
        <Button type="submit" kind="primary" disabled={submit.isPending}>
          {t('intake.submit')}
        </Button>
      </div>
    </form>
  );
}
